import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from 'prop-types';
import {
  Row, Col, Table, Spin, Progress, Tooltip, Modal, Input, List, Button, DatePicker, Select
} from 'antd';
import systemActions from '../../../redux/systemInfos/actions';
import { DvIntlMessages } from "../../../components/utility/dvGeneralUtilities";
import { ExclamationCircleTwoTone } from '@ant-design/icons';
import IntlMessages from "../../../components/utility/intlMessages";
import moment from "moment";

const { getUserBackgroundJobs, resetUserBackgroundJobs } = systemActions;

const DATE_FORMAT = 'DD/MM/YYYY';
const DEFAULT_RECORDS_PER_PAGE = 20;
const DEFAULT_PAGE_SIZE_OPTIONS = [20, 30, 40, 50];
const INIT_PAGE = 1;
const COLORS = {
  "INFO": "#6666ff",
  "ERROR": "#e50000",
  "DEBUG": "#329932",
  "WARNING": "#FFA500"
};

function UserBackgroundJobs(props) {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState([]);
  const [currentPage, setCurrentPage] = useState(INIT_PAGE);
  const [recordsPerPage, setRecordsPerPage] = useState(DEFAULT_RECORDS_PER_PAGE);
  const [jobLevel, setJobLevel] = useState(null);
  const [jobType, setJobType] = useState(null);
  const [jobsDate, setJobDate] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const filters = {
      "limit": recordsPerPage,
      "page": currentPage,
      "level": jobLevel,
      "type": jobType,
      "date": jobsDate
    }
    setUserId(props.match.params.userId);

    props.getUserBackgroundJobs(props.match.params.userId, filters)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.match.params?.userId])

  function openModal(record) {
    setSelectedRecord(record)
    setModalVisible(visible => !visible);
  }

  function closeModal() {
    setSelectedRecord([])
    setModalVisible(visible => !visible);
  }

  function handleOnChangeDate(value) {
    if (value) setJobDate(moment(value).format(DATE_FORMAT));
    else setJobDate('');
  }

  function handleOnChangeType(e) {
    const { value } = e.target;
    setJobType(value ? value : null)
  }

  function submitFilters() {
    props.resetUserBackgroundJobs();
    setCurrentPage(INIT_PAGE);
    const filters = {
      "limit": recordsPerPage,
      "jobsPage": INIT_PAGE,
      "level": jobLevel,
      "type": jobType,
      "date": jobsDate
    }
    props.getUserBackgroundJobs(userId, filters);
  }

  function updateTableData(pageData) {
    const { current, pageSize } = pageData;
    let newPageNumber = current;

    if (pageSize !== recordsPerPage) newPageNumber = INIT_PAGE;


    const filters = {
      "limit": pageSize,
      "page": newPageNumber,
      "level": jobLevel,
      "type": jobType,
      "date": jobsDate
    }
    props.getUserBackgroundJobs(userId, filters);
    setCurrentPage(newPageNumber);
    setRecordsPerPage(pageSize);
  }

  const getLevel = (level) => {
    const color = COLORS[level];
    return <div style={{ color: color }}>{level}</div>
  }

  const getProgressStatus = (level, percentage) => {
    let status;

    const mapper = {
      "INFO": "active",
      "ERROR": "exception"
    }

    if (percentage === 100) {
      status = "success";
    } else {
      status = mapper[level];
    }
    return status;
  }

  const columns = [
    {
      key: "type",
      title: <IntlMessages id="homePage.backgrounLogs.type" />,
      render: (text, record) => {
        return record.logs[0].type
      }
    },
    {
      key: "id",
      title: <IntlMessages id="homePage.backgrounLogs.jobId" />,
      render: (text, record) => {
        return record.logs[0].id
      }
    },
    {
      key: "level",
      title: <IntlMessages id="databases.status" />,
      render: (text, record) => {
        return (
          <Row>
            {record.containsError &&
              <Col span={1}>
                <Tooltip title={<IntlMessages id="homePage.backgrounLogs.containsError" />}>
                  <ExclamationCircleTwoTone twoToneColor={'red'} />
                </Tooltip>
              </Col>
            }
            <Col>
              {getLevel(record.logs[0].level)}
            </Col>
          </Row>
        )
      }
    },
    {
      key: "status",
      title: <IntlMessages id="homePage.backgrounLogs.status" />,
      render: (text, record) => {
        return record.logs[0].status
      }
    },
    {
      key: "moreInfo",
      render: (text, record) => {
        const progressStatus = getProgressStatus(record.logs[0].level, record.logs[0].percentage);
        return (
          <Row gutter={[16, 16]}>
            <Col span={24} style={{ wordWrap: "break-word" }}>
              <IntlMessages id="homePage.backgrounLogs.userId" />: {record.logs[0].userId}
            </Col>
            <Col span={24} style={{ wordWrap: "break-word" }}>
              <IntlMessages id="homePage.backgrounLogs.datetime" />: {record.logs[0].datetime}
            </Col>
            <Col span={23} style={{ wordWrap: "break-word" }}>
              <Progress percent={record.logs[0].percentage} status={progressStatus} />
            </Col>
          </Row>
        );
      }
    },
  ];

  function backgroundJobLogListItemFactory(record) {
    const progressStatus = getProgressStatus(record.level, record.percentage);

    return (
      <List.Item>
        <Row style={{ width: "100%" }} gutter={[16, 16]}>
          <Col span={5} style={{ wordWrap: "break-word" }}>
            <IntlMessages id="homePage.backgrounLogs.type" />: {record.type}
          </Col>
          <Col span={2} style={{ wordWrap: "break-word" }}>
            <IntlMessages id="homePage.backgrounLogs.jobId" />: {record.id}
          </Col>
          <Col span={2} style={{ wordWrap: "break-word" }}>
            {getLevel(record.level)}
          </Col>
          <Col span={8} style={{ wordWrap: "break-word" }}>
            <IntlMessages id="homePage.backgrounLogs.status" />: {record.status}
          </Col>
          <Col span={7}>
            <Row gutter={[16, 16]}>
              <Col span={24} style={{ wordWrap: "break-word" }}>
                <IntlMessages id="homePage.backgrounLogs.userId" />: {record.user_id}
              </Col>
              <Col span={24} style={{ wordWrap: "break-word" }}>
                <IntlMessages id="homePage.backgrounLogs.datetime" />: {record.datetime}
              </Col>
              <Col span={24} style={{ wordWrap: "break-word" }}>
                <Progress percent={record.percentage} status={progressStatus} />
              </Col>
            </Row>
          </Col>
        </Row>
      </List.Item>
    )
  }

  const tableColumns = columns.map(item => ({ ...item, ellipsis: true }));
  const paginationConfig = {
    showSizeChanger: true,
    defaultPageSize: recordsPerPage,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    current: currentPage,
    total: props.numberOfLogs
  };
  return (
    <Spin spinning={props.loading > 0}>
      <Row gutter={[16, 16]} style={{ marginTop: "25px" }}>

      <Col span={24}>
        <Row gutter={16}>
          <Col>
            <Input
              placeholder={DvIntlMessages("homePage.backgrounLogs.type")}
              allowClear onChange={handleOnChangeType}
            />
          </Col>
          <Col>
            <Select style={{ width: 120 }} onChange={setJobLevel}
              placeholder={DvIntlMessages("databases.status")} allowClear={true}>
              {Object.keys(COLORS).map(value => {
                return (
                  <Select.Option value={value} key={value}>
                    {value}
                  </Select.Option>
                )
              })}
            </Select>
          </Col>
          <Col>
            <DatePicker
              format={DATE_FORMAT}
              placeholder={DvIntlMessages("homePage.backgrounLogs.datetime")}
              allowClear onChange={handleOnChangeDate} enterButton
            />
          </Col>
          <Col>
            <Button onClick={submitFilters} type='primary'>
              <IntlMessages id="general.submit" />
            </Button>
          </Col>
        </Row>
      </Col>

        <Col span={24}>
          <Table
            bordered={true}
            showHeader
            pagination={paginationConfig}
            columns={tableColumns}
            dataSource={props.data}
            onChange={updateTableData}
            rowKey={(record) => record.logs[0].id}
            onRow={(record, rowIndex) => {
              return {
                onClick: event => { openModal(record['logs']) },
              };
            }}
          />
        </Col>

        <Modal visible={modalVisible} onCancel={closeModal} onOk={closeModal} width={1200} closable={false} footer={null}>
          <List
            dataSource={selectedRecord}
            bordered
            renderItem={(record) => backgroundJobLogListItemFactory(record)}
          />
        </Modal>
      </Row>
    </Spin>
  )
}

UserBackgroundJobs.propTypes = {
  data: PropTypes.array.isRequired,
  loading: PropTypes.number.isRequired,
  numberOfLogs: PropTypes.number.isRequired,

  getUserBackgroundJobs: PropTypes.func.isRequired,
  resetUserBackgroundJobs: PropTypes.func.isRequired
};

UserBackgroundJobs.defaultProps = {
  userDetails: []
}

const mapActionsToProps = {
  getUserBackgroundJobs,
  resetUserBackgroundJobs
};

function mapStateToProps(state) {
  const userBackgroundJobs = state.SystemInfos.get('userBackgroundJobs');
  return {
    data: userBackgroundJobs?.logs ?? [],
    numberOfLogs: userBackgroundJobs?.numberOfLogs,
    loading: state.SystemInfos.get('loading')
  };
}

const wrappedUserBackgroundJobs = withRouter(UserBackgroundJobs);
const connectedUserBackgroundJobs = connect(mapStateToProps, mapActionsToProps)(wrappedUserBackgroundJobs);
export default connectedUserBackgroundJobs;